import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { CONDITION_PARAMS, OPERATOR_LABELS } from '../../lib/ab-test-params';
import type { ABVariant, ABVariantRule, ABTargetMode } from '../../types/campaign';

const MAX_VARIANTS = 5;

// Generate a simple unique ID for new variants
function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

// Accent colours cycling per variant index
const VARIANT_COLORS = [
  'border-l-indigo-500',
  'border-l-emerald-500',
  'border-l-violet-500',
  'border-l-amber-500',
  'border-l-rose-500',
  'border-l-cyan-500',
];

const VARIANT_DOT_COLORS = [
  'bg-indigo-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-cyan-500',
];

interface ConditionRuleRowProps {
  variantIndex: number;
  ruleIndex: number;
  rule: ABVariantRule;
  onUpdate: (field: keyof ABVariantRule, value: unknown) => void;
  onRemove: () => void;
}

function ConditionRuleRow({ variantIndex, ruleIndex, rule, onUpdate, onRemove }: ConditionRuleRowProps) {
  const paramConfig = CONDITION_PARAMS[rule.param];
  const [tagInput, setTagInput] = useState('');

  const addValue = (val: string) => {
    const trimmed = val.trim();
    if (trimmed && !rule.values.includes(trimmed)) {
      onUpdate('values', [...rule.values, trimmed]);
    }
    setTagInput('');
  };

  const removeValue = (val: string) => {
    onUpdate('values', rule.values.filter(v => v !== val));
  };

  return (
    <div className="flex items-start gap-2 group">
      {/* Param selector */}
      <select
        value={rule.param}
        onChange={e => {
          onUpdate('param', e.target.value);
          onUpdate('values', []);
          const newConfig = CONDITION_PARAMS[e.target.value];
          onUpdate('operator', newConfig?.operators[0] ?? 'in');
        }}
        className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white text-gray-700 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 min-w-[120px]"
      >
        {Object.entries(CONDITION_PARAMS).map(([key, cfg]) => (
          <option key={key} value={key}>{cfg.label}</option>
        ))}
      </select>

      {/* Operator selector */}
      <select
        value={rule.operator}
        onChange={e => onUpdate('operator', e.target.value)}
        className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white text-gray-700 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 min-w-[100px]"
      >
        {(paramConfig?.operators ?? ['in', 'not_in']).map(op => (
          <option key={op} value={op}>{OPERATOR_LABELS[op] ?? op}</option>
        ))}
      </select>

      {/* Values */}
      <div className="flex-1 min-w-0">
        {paramConfig?.valueType === 'multiselect' ? (
          <div className="flex flex-wrap gap-1 items-center border border-gray-200 rounded-md px-2 py-1 bg-white min-h-[32px]">
            {rule.values.map(val => {
              const label = paramConfig.options?.find(o => o.value === val)?.label ?? val;
              return (
                <span key={val} className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 text-xs rounded px-1.5 py-0.5">
                  {label}
                  <button type="button" onClick={() => removeValue(val)} className="hover:text-indigo-900 leading-none">×</button>
                </span>
              );
            })}
            <select
              value=""
              onChange={e => { if (e.target.value) addValue(e.target.value); }}
              className="text-xs text-gray-400 bg-transparent border-none outline-none cursor-pointer"
            >
              <option value="">+ Add</option>
              {(paramConfig?.options ?? [])
                .filter(o => !rule.values.includes(o.value))
                .map(o => <option key={o.value} value={o.value}>{o.label}</option>)
              }
            </select>
          </div>
        ) : (
          <input
            type="text"
            value={tagInput}
            onChange={e => setTagInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addValue(tagInput); } }}
            onBlur={() => { if (tagInput) addValue(tagInput); }}
            placeholder={paramConfig?.placeholder ?? 'Type value and press Enter'}
            className="w-full text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
          />
        )}
      </div>

      {/* Remove rule button */}
      <button
        type="button"
        onClick={onRemove}
        className="mt-1 text-gray-300 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
        title="Remove condition"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

interface VariantCardProps {
  index: number;
  variant: ABVariant;
  mode: ABTargetMode;
  totalWeight: number;
  onUpdate: (updates: Partial<ABVariant>) => void;
  onRemove: () => void;
  canRemove: boolean;
}

function VariantCard({ index, variant, mode, totalWeight, onUpdate, onRemove, canRemove }: VariantCardProps) {
  const { t } = useTranslation('campaigns');
  const colorClass = VARIANT_COLORS[index % VARIANT_COLORS.length];
  const dotColor = VARIANT_DOT_COLORS[index % VARIANT_DOT_COLORS.length];
  const weightPct = totalWeight > 0 ? Math.round((variant.weight / totalWeight) * 100) : 0;

  const rules = variant.conditions?.rules ?? [];
  const matchMode = variant.conditions?.match ?? 'all';

  const updateRule = (ruleIndex: number, field: keyof ABVariantRule, value: unknown) => {
    const newRules = [...rules];
    newRules[ruleIndex] = { ...newRules[ruleIndex], [field]: value };
    onUpdate({ conditions: { match: matchMode, rules: newRules } });
  };

  const addRule = () => {
    const firstParam = Object.keys(CONDITION_PARAMS)[0];
    const firstOp = CONDITION_PARAMS[firstParam]?.operators[0] ?? 'in';
    const newRules = [...rules, { param: firstParam, operator: firstOp, values: [] } as ABVariantRule];
    onUpdate({ conditions: { match: matchMode, rules: newRules } });
  };

  const removeRule = (ruleIndex: number) => {
    const newRules = rules.filter((_, i) => i !== ruleIndex);
    onUpdate({ conditions: newRules.length > 0 ? { match: matchMode, rules: newRules } : null });
  };

  const isFallback = !variant.conditions || rules.length === 0;

  return (
    <div className={`bg-white rounded-lg border border-gray-200 border-l-4 ${colorClass} overflow-hidden`}>
      {/* Card header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-100">
        <div className={`w-2 h-2 rounded-full ${dotColor} flex-shrink-0`} />
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('ab_test.variant_label', { number: index + 1 })}</span>

        {/* Name input */}
        <input
          type="text"
          value={variant.name}
          onChange={e => onUpdate({ name: e.target.value })}
          placeholder={t('ab_test.variant_name_placeholder')}
          className="flex-1 text-sm font-medium text-gray-800 bg-transparent border-none outline-none placeholder:text-gray-400 placeholder:font-normal"
        />

        {/* Weight badge (weighted mode only) */}
        {mode === 'split_params_percnt' && (
          <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
            {weightPct}%
          </span>
        )}

        {/* Fallback badge */}
        {isFallback && (
          <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
            {t('ab_test.fallback_badge')}
          </span>
        )}

        {/* Remove button */}
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="ml-auto text-gray-300 hover:text-red-400 transition-colors p-0.5"
            title="Remove variant"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>

      {/* Card body */}
      <div className="px-4 py-3 space-y-3">
        {/* URL input */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{t('ab_test.destination_url')}</label>
          <input
            type="text"
            value={variant.url}
            onChange={e => onUpdate({ url: e.target.value })}
            placeholder={t('ab_test.destination_url_placeholder')}
            className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
          />
        </div>

        {/* Weight slider (weighted mode) */}
        {mode === 'split_params_percnt' && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-gray-500">{t('ab_test.traffic_weight')}</label>
              <span className="text-xs font-semibold text-indigo-600">{variant.weight}%</span>
            </div>
            <input
              type="range"
              min={1}
              max={100}
              value={variant.weight}
              onChange={e => onUpdate({ weight: parseInt(e.target.value) })}
              className="w-full h-1.5 bg-gray-200 rounded-full appearance-none cursor-pointer accent-indigo-600"
            />
          </div>
        )}

        {/* Conditions section (condition-based mode) */}
        {mode === 'split_params' && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-500">
                {isFallback ? t('ab_test.conditions_fallback_label') : t('ab_test.conditions_label')}
              </label>
              {rules.length > 1 && (
                <div className="flex items-center gap-1 text-xs">
                  <span className="text-gray-400">{t('ab_test.match_label')}</span>
                  <button
                    type="button"
                    onClick={() => onUpdate({ conditions: { match: 'all', rules } })}
                    className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${matchMode === 'all' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    {t('ab_test.match_all')}
                  </button>
                  <span className="text-gray-300">/</span>
                  <button
                    type="button"
                    onClick={() => onUpdate({ conditions: { match: 'any', rules } })}
                    className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${matchMode === 'any' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    {t('ab_test.match_any')}
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-2">
              {rules.map((rule, ruleIndex) => (
                <ConditionRuleRow
                  key={ruleIndex}
                  variantIndex={index}
                  ruleIndex={ruleIndex}
                  rule={rule}
                  onUpdate={(field, value) => updateRule(ruleIndex, field, value)}
                  onRemove={() => removeRule(ruleIndex)}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={addRule}
              className="mt-2 text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              {t('ab_test.add_condition')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface ABTestBuilderProps {
  disabled?: boolean;
}

export function ABTestBuilder({ disabled }: ABTestBuilderProps) {
  const { t } = useTranslation('campaigns');
  const { watch, setValue } = useFormContext();

  const targetMode: ABTargetMode = watch('target_mode') || 'default';
  const splitLinks: ABVariant[] = watch('split_links') || [];

  const isEnabled = targetMode !== 'default' && targetMode != null;

  const setMode = (mode: ABTargetMode) => setValue('target_mode', mode, { shouldDirty: true });
  const setVariants = (variants: ABVariant[]) => setValue('split_links', variants, { shouldDirty: true });

  const enable = () => {
    setMode('split_params');
    if (splitLinks.length === 0) {
      setVariants([
        { id: generateId(), name: t('ab_test.variant_label', { number: 1 }), url: '', weight: 50, conditions: null },
        { id: generateId(), name: t('ab_test.variant_label', { number: 2 }), url: '', weight: 50, conditions: null },
      ]);
    }
  };

  const disable = () => {
    setMode('default');
  };

  const addVariant = () => {
    if (splitLinks.length >= MAX_VARIANTS) return;
    setVariants([
      ...splitLinks,
      { id: generateId(), name: t('ab_test.variant_label', { number: splitLinks.length + 1 }), url: '', weight: 50, conditions: null },
    ]);
  };

  const updateVariant = (index: number, updates: Partial<ABVariant>) => {
    const newVariants = splitLinks.map((v, i) => i === index ? { ...v, ...updates } : v);
    setVariants(newVariants);
  };

  const removeVariant = (index: number) => {
    setVariants(splitLinks.filter((_, i) => i !== index));
  };

  const totalWeight = splitLinks.reduce((sum, v) => sum + (v.weight || 0), 0);

  const hasFallback = targetMode === 'split_params'
    ? splitLinks.some(v => !v.conditions || v.conditions.rules.length === 0)
    : true;

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Header with toggle */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
            <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{t('ab_test.title')}</h3>
            <p className="text-xs text-gray-500">{t('ab_test.subtitle')}</p>
          </div>
        </div>

        {/* Toggle */}
        <button
          type="button"
          onClick={isEnabled ? disable : enable}
          disabled={disabled}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 ${
            isEnabled ? 'bg-indigo-600' : 'bg-gray-200'
          }`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            isEnabled ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      </div>

      {/* Body — only shown when enabled */}
      {isEnabled && (
        <div className="px-5 py-4 space-y-4">
          {/* Mode selector */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">{t('ab_test.distribution_mode')}</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode('split_params')}
                className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-all ${
                  targetMode === 'split_params'
                    ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  targetMode === 'split_params' ? 'border-indigo-500' : 'border-gray-300'
                }`}>
                  {targetMode === 'split_params' && <div className="w-2 h-2 rounded-full bg-indigo-500" />}
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-800">{t('ab_test.condition_based')}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{t('ab_test.condition_based_desc')}</div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setMode('split_params_percnt')}
                className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-all ${
                  targetMode === 'split_params_percnt'
                    ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  targetMode === 'split_params_percnt' ? 'border-indigo-500' : 'border-gray-300'
                }`}>
                  {targetMode === 'split_params_percnt' && <div className="w-2 h-2 rounded-full bg-indigo-500" />}
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-800">{t('ab_test.weighted_random')}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{t('ab_test.weighted_random_desc')}</div>
                </div>
              </button>
            </div>
          </div>

          {/* Weight distribution bar (weighted mode) */}
          {targetMode === 'split_params_percnt' && splitLinks.length > 0 && (
            <div>
              <div className="flex h-2 rounded-full overflow-hidden gap-0.5">
                {splitLinks.map((v, i) => {
                  const pct = totalWeight > 0 ? (v.weight / totalWeight) * 100 : 100 / splitLinks.length;
                  const color = ['bg-indigo-500', 'bg-emerald-500', 'bg-violet-500', 'bg-amber-500', 'bg-rose-500', 'bg-cyan-500'][i % 6];
                  return <div key={v.id} className={`${color} transition-all`} style={{ width: `${pct}%` }} />;
                })}
              </div>
              <div className="flex justify-between mt-1">
                {splitLinks.map((v, i) => {
                  const pct = totalWeight > 0 ? Math.round((v.weight / totalWeight) * 100) : Math.round(100 / splitLinks.length);
                  return (
                    <span key={v.id} className="text-xs text-gray-400">
                      {v.name || t('ab_test.variant_label', { number: i + 1 })}: {pct}%
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Variant cards */}
          <div className="space-y-3">
            {splitLinks.map((variant, index) => (
              <VariantCard
                key={variant.id}
                index={index}
                variant={variant}
                mode={targetMode}
                totalWeight={totalWeight}
                onUpdate={updates => updateVariant(index, updates)}
                onRemove={() => removeVariant(index)}
                canRemove={splitLinks.length > 1}
              />
            ))}
          </div>

          {/* Add variant button */}
          {splitLinks.length < MAX_VARIANTS ? (
            <button
              type="button"
              onClick={addVariant}
              className="w-full flex items-center justify-center gap-2 py-2.5 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              {t('ab_test.add_variant', { count: splitLinks.length, max: MAX_VARIANTS })}
            </button>
          ) : (
            <div className="w-full flex items-center justify-center gap-2 py-2.5 border border-dashed border-gray-200 rounded-lg text-sm text-gray-400 cursor-not-allowed bg-gray-50">
              {t('ab_test.max_variants_reached', { max: MAX_VARIANTS })}
            </div>
          )}

          {/* Fallback warning */}
          {targetMode === 'split_params' && !hasFallback && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-xs text-amber-700">
                <strong>{t('ab_test.no_fallback_title')}</strong> {t('ab_test.no_fallback_desc')}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
